import React from 'react';
import {
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Divider,
    Box,
    alpha,
    useTheme,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Campaign as CampaignIcon,
    People as PeopleIcon,
    BarChart as BarChartIcon,
    MoreHoriz as MoreIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { filterNavItems } from './navVisibility';
import type { NavGroup } from './navVisibility';
import { useFeatureFlags } from '../../hooks/useFeatureFlag';

interface MobileBottomNavProps {
    navGroups: NavGroup[];
    isAdmin: boolean;
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ navGroups, isAdmin }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [moreOpen, setMoreOpen] = React.useState(false);
    // Vlastní volání hooku místo prop-drillingu — RQ cache ['appConfig'] je sdílená s AdminLayoutem.
    const { isFeatureEnabled } = useFeatureFlags();

    const mainTabs = React.useMemo(() => [
        { label: 'Dashboard', path: '/admin', icon: <DashboardIcon /> },
        ...(isAdmin ? [{ label: 'Poplach', path: '/admin/poplach', icon: <CampaignIcon /> }] : []),
        { label: 'Jednotka', path: '/admin/users', icon: <PeopleIcon /> },
        { label: 'Přehledy', path: '/admin/statistiky', icon: <BarChartIcon /> },
    ], [isAdmin]);

    const currentTab = mainTabs.findIndex((tab) => location.pathname === tab.path);
    const moreIndex = mainTabs.length;
    const isMoreActive = currentTab === -1 && location.pathname.startsWith('/admin');

    return (
        <>
            <Paper
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: theme.zIndex.appBar,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    borderRadius: 0,
                }}
                elevation={8}
            >
                <BottomNavigation
                    value={isMoreActive ? moreIndex : currentTab}
                    onChange={(_event, newValue) => {
                        if (newValue === moreIndex) {
                            setMoreOpen(true);
                        } else {
                            navigate(mainTabs[newValue].path);
                        }
                    }}
                    showLabels
                    sx={{
                        height: 64,
                        '& .MuiBottomNavigationAction-root': {
                            minWidth: 'auto',
                            py: 1,
                            '&.Mui-selected': {
                                color: theme.palette.primary.main,
                            },
                        },
                        '& .MuiBottomNavigationAction-label': {
                            fontSize: '0.6875rem',
                            '&.Mui-selected': {
                                fontSize: '0.6875rem',
                            },
                        },
                    }}
                >
                    {mainTabs.map((tab) => (
                        <BottomNavigationAction
                            key={tab.path}
                            label={tab.label}
                            icon={tab.icon}
                        />
                    ))}
                    <BottomNavigationAction label="Více" icon={<MoreIcon />} />
                </BottomNavigation>
            </Paper>

            <Drawer
                anchor="bottom"
                open={moreOpen}
                onClose={() => setMoreOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '20px 20px 0 0',
                        maxHeight: '70vh',
                    },
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 4,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.text.primary, 0.2),
                        mx: 'auto',
                        mt: 1.5,
                        mb: 1,
                    }}
                />
                <List sx={{ pb: 2 }}>
                    {navGroups.map((group) => {
                        const visibleItems = filterNavItems(group.items, isAdmin, isFeatureEnabled);
                        if (visibleItems.length === 0) return null;
                        return (
                            <React.Fragment key={group.label ?? '_top'}>
                                {group.label && (
                                    <ListSubheader sx={{ bgcolor: 'transparent' }}>
                                        {group.label}
                                    </ListSubheader>
                                )}
                                {visibleItems.map((item) => (
                                    <ListItem key={item.path || item.label} disablePadding>
                                        <ListItemButton
                                            selected={location.pathname === item.path}
                                            onClick={() => {
                                                if (item.path) {
                                                    navigate(item.path);
                                                    setMoreOpen(false);
                                                }
                                            }}
                                        >
                                            <ListItemIcon>{item.icon}</ListItemIcon>
                                            <ListItemText primary={item.label} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                                <Divider sx={{ my: 0.5 }} />
                            </React.Fragment>
                        );
                    })}
                </List>
            </Drawer>
        </>
    );
};

export default MobileBottomNav;
